import React from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Label,
  Row,
} from "reactstrap";
import { BaseInput, ErrorMessage } from "Components/ui";
import Flatpickr from "react-flatpickr";
import { useFormik } from "formik";
import { ExpenseValidationSchema } from "utils";
import { useCreateExpense } from "hooks/useExpenses";
import { toast } from "react-toastify";
import Breadcrumbs from "../../Components/Common/Breadcrumb";
import { useNavigate } from "react-router-dom";
import { AppRoutes } from "../../constants";
import { useGetExpenseCategories } from "hooks/useExpenseCategory";
import CreatableSelect from "react-select/creatable";

const AddExpense = () => {
  const { mutate, isPending } = useCreateExpense();
  document.title = "Papermap | Add Expense";

  const navigate = useNavigate();
  const { data: expenseCategories } = useGetExpenseCategories();

  const formik = useFormik({
    initialValues: {
      amount: 0,
      description: "",
      date: "",
      category: { id: "", name: "" },
    },
    validationSchema: ExpenseValidationSchema,
    onSubmit: async (values) => {
      mutate(
        {
          amount: values.amount,
          description: values.description,
          date: values.date,
          category: values.category,
        },
        {
          onSuccess: () => {
            navigate(AppRoutes.EXPENSES);
            toast.success("Expense created successfully.");
            formik.resetForm();
          },
        }
      );
    },
  });

  const handleCancel = () => {
    navigate(AppRoutes.EXPENSES);
  };

  return (
    <React.Fragment>
      <div className={`page-content`}>
        <Container fluid>
          {/* Breadcrumb */}
          <Breadcrumbs title="Expenses" breadcrumbItem="Add Expense" />

          <Form onSubmit={formik.handleSubmit} autoComplete="off">
            <Row>
              <Col xs={12}>
                <Card>
                  <CardBody>
                    <Row>
                      <Col sm="12">
                        <div className="mb-3">
                          <BaseInput
                            label="Description"
                            name="description"
                            type="textarea"
                            value={formik.values.description}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={
                              formik.touched.description &&
                              !!formik.errors.description
                            }
                            errorMessage={formik.errors.description}
                          />
                        </div>
                      </Col>
                      <Col sm="6">
                        <div className="mb-3">
                          <BaseInput
                            label="Amount*"
                            name="amount"
                            type="number"
                            min="0"
                            value={formik.values.amount}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={
                              formik.touched.amount && !!formik.errors.amount
                            }
                            errorMessage={formik.errors.amount}
                          />
                        </div>
                      </Col>
                      <Col sm="6">
                        <div className="mb-3">
                          <label htmlFor="Category">Category</label>
                          <CreatableSelect
                            placeholder="Select or create a category"
                            isClearable
                            isLoading={isPending}
                            // onCreateOption={handleCreate}
                            options={
                              expenseCategories &&
                              expenseCategories.map((item) => {
                                return { value: item.id, label: item.name };
                              })
                            }
                            onChange={(
                              selectedOption: {
                                value: string | number;
                                label: string;
                              } | null
                            ) => {
                              const categoryId = selectedOption
                                ? selectedOption.value
                                : "";
                              const categoryName = selectedOption
                                ? selectedOption.label
                                : "";

                              const categoryExists = expenseCategories?.some(
                                (item) => item.id === categoryId
                              );
                              formik.setFieldValue("category", {
                                id: categoryExists ? categoryId : 0,
                                name: categoryName,
                              });
                            }}
                          />
                          {formik.touched.category &&
                            formik.errors.category && (
                              <ErrorMessage>
                                {typeof formik.errors.category === "string"
                                  ? formik.errors.category
                                  : formik.errors.category?.name}
                              </ErrorMessage>
                            )}
                        </div>
                      </Col>
                      <Col sm="12">
                        <div className="mb-3">
                          <Label htmlFor="date">Date</Label>
                          <Flatpickr
                            id="date"
                            name="date"
                            value={formik.values.date || undefined}
                            onChange={(date: Date[]) => {
                              const formattedDate = date.length
                                ? date[0].toISOString().split("T")[0]
                                : null;
                              formik.setFieldValue("date", formattedDate);
                            }}
                            options={{
                              altInput: true,
                              altFormat: "F j, Y",
                              dateFormat: "Y-m-d",
                            }}
                            className="form-control"
                          />
                          {formik.touched.date && formik.errors.date && (
                            <ErrorMessage>{formik.errors.date}</ErrorMessage>
                          )}
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>
            {/* Button Section */}
            <div className="d-flex justify-content-end mt-3">
              <Button
                type="submit"
                color="primary"
                className="w-20 me-1"
                style={{ width: "20%" }}
              >
                {isPending ? "Submitting ..." : "Submit"}
              </Button>
              <Button
                color="secondary"
                className="w-20 ms-1"
                style={{ width: "20%" }}
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </div>
          </Form>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default AddExpense;
